import React, { useState } from "react";
import { useSelector } from "react-redux";
import * as dateFns from "date-fns";

// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  DataTableMessages,
  CanHistoryMessages,
  VehiclesStatesTypesMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages

// region Imports - Interfaces
import { CanReportData } from "@shared/interfaces/can-report.interface";
// endregion Imports - Interfaces

// region Imports - Utils
import utils from "@utils/useful-functions";
import { formatDecimalHours, formatOdometer } from "@shared/utils/useful-functions";
// endregion Imports - Utils

// region Imports - Components
import Header from "@components/Header";
import NewDataTable, { DataTableColumns, DataTableButtons } from "@molecules/NewDataTable";
import QueryFilterCanReport from "@components/organisms/QueryFilterCanReport";
// endregion Imports - Components

// region Imports - Styles
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { Container } from "./styles";
// endregion Imports - Styles

/**
 * Renders a CAN reading, showing "-" when the tracker never reported the field.
 * A missing reading must NEVER be rendered as 0: on a CAN report a zero reads as a real measurement
 * (e.g. the total driving time is reported by Queclink trackers only).
 * @param value CAN value coming from the report (null when unavailable)
 * @param format Formatter applied when the value exists
 */
const renderCanValue = (value: number | null, format: (canValue: number) => string): string => (
  typeof value === "number" && Number.isFinite(value) ? format(value) : "-"
);

interface CanHistoryProps {
  /** Rendered inside the CAN consumption tabs, which already draw the page header */
  embedded?: boolean;
}

const CanHistory: React.FC<CanHistoryProps> = ({ embedded = false }) => {

  // region States
  const [canHistoryData, setCanHistoryData] = useState<CanReportData[]>([]);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);
  const [openScheduleReportModal, setOpenScheduleReportModal] = useState(false);

  const [startDate, setStartDate] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00")
  );
  const [finishDate, setFinishDate] = useState<string>(
    dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59")
  );

  // endregion States

  // region Hooks
  const { screen } = useSelector((screen) => screen);
  const { t } = useTranslation();
  // endregion Hooks

  // region Constants
  const dataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };

  const canHistoryColumns: DataTableColumns[] = [
    {
      title: t(CanHistoryMessages.dateTime),
      data: (d: CanReportData) => {
        const dateTime = dateFns.format(new Date(d.occurrenceDate), "dd/MM/yyyy HH:mm:ss");

        if (d.isNormalized) return dateTime;

        return `${dateTime} <span title="${t(CanHistoryMessages.rawDataTooltip)}" style="opacity: 0.5; cursor: help;">*</span>`;
      },
      propertyName: "occurrenceDate",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.fleet),
      data: (d: CanReportData) => d.vehicleCode || "-",
      propertyName: "vehicleCode",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.plate),
      data: (d: CanReportData) => d.vehiclePlate || "-",
      propertyName: "vehiclePlate",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.travelDoc),
      data: (d: CanReportData) => d.travelDoc || "-",
      propertyName: "travelDoc",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.status),
      data: (d: CanReportData) => {
        if (d.statusId && VehiclesStatesTypesMessages[d.statusId]) {
          return t(VehiclesStatesTypesMessages[d.statusId]);
        }

        return d.status || "-";
      },
      propertyName: "status",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.driver),
      data: (d: CanReportData) => d.driverName || "-",
      propertyName: "driverName",
      filterable: false
    },
    {
      // Absent CAN_IGN is "unknown", not "off" — it must read "-" like any other missing CAN value.
      title: t(CanHistoryMessages.ignition),
      data: (d: CanReportData) => {
        if (d.ignition === null || d.ignition === undefined) return "-";

        return d.ignition ? t(CanHistoryMessages.ignitionOn) : t(CanHistoryMessages.ignitionOff);
      },
      propertyName: "ignition",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.odometer),
      data: (d: CanReportData) => renderCanValue(d.odometer, (odometer) => formatOdometer(odometer)),
      propertyName: "odometer",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.totalFuel),
      data: (d: CanReportData) => renderCanValue(d.totalFuelUsed, (fuel) => `${utils.formatNumberToLocaleString(fuel)} L`),
      propertyName: "totalFuelUsed",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.speed),
      data: (d: CanReportData) => renderCanValue(d.speed, (speed) => `${utils.formatNumberToLocaleString(speed)} Km/h`),
      propertyName: "speed",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.rpm),
      data: (d: CanReportData) => renderCanValue(d.rpm, (rpm) => utils.formatNumberToLocaleString(rpm)),
      propertyName: "rpm",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.coolantTemp),
      data: (d: CanReportData) => renderCanValue(d.coolantTemperature, (temp) => `${utils.formatNumberToLocaleString(temp)} °C`),
      propertyName: "coolantTemperature",
      filterable: false
    },
    {
      // The value carries its own unit as a prefix ("H" = L/h, "M" = L/100 km) — never assume L/h.
      title: t(CanHistoryMessages.fuelConsumption),
      data: (d: CanReportData) => utils.formatFuelConsumption(d.fuelConsumption) || "-",
      propertyName: "fuelConsumption",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.fuelLevel),
      data: (d: CanReportData) => utils.formatFuelLevel(d.fuelLevel) || "-",
      propertyName: "fuelLevel",
      filterable: false
    },
    {
      // CAN_TOTAL_ENGINE_HOURS is reported in decimal HOURS, not minutes.
      title: t(CanHistoryMessages.hourmeter),
      data: (d: CanReportData) => renderCanValue(d.hourmeter, (hours) => formatDecimalHours(hours)),
      propertyName: "hourmeter",
      filterable: false
    },
    {
      // CAN_TOTAL_DRIVING_TIME is reported in decimal HOURS: render it like the hourmeter column so
      // both cumulative times are directly comparable and carry their unit.
      title: t(CanHistoryMessages.totalDrivingTime),
      data: (d: CanReportData) => renderCanValue(d.totalDrivingTime, (hours) => formatDecimalHours(hours)),
      propertyName: "totalDrivingTime",
      filterable: false
    },
    {
      title: t(CanHistoryMessages.idleFuel),
      data: (d: CanReportData) => renderCanValue(d.totalIdleFuelUsed, (fuel) => `${utils.formatNumberToLocaleString(fuel)} L`),
      propertyName: "totalIdleFuelUsed",
      filterable: false
    }

  ];

  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: { columns: "th:not(:last-child)" }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `historico_can_${startDate}__${finishDate}`,
      exportOptions: { columns: "th:not(:last-child)" }
    }
  ];
  // endregion Constants

  // region Functions
  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: CanReportData[],
    startDate: string,
    finishDate: string
  ) => {
    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setCanHistoryData(returnedData);
    setStartDate(startDate);
    setFinishDate(finishDate);
  };

  // endregion Functions

  return (
    <>
      {!embedded && <Header title={t(CanHistoryMessages.title)} />}
      <Container platform={screen.platform as ScreenPlatform} className="page" embedded={embedded}>
        <div className="data-table-container">
          <NewDataTable
            title={t(CanHistoryMessages.title)}
            data={canHistoryData}
            columns={canHistoryColumns}
            settings={dataTableSettings}
            buttons={dataTableButtons}
            filters
            onClickFilterButton={() => setOpenFilterFields(true)}
            numberOfFilteredOptions={numberOfFilteredOptions}
          />
        </div>
        <QueryFilterCanReport
          open={openFilterFields}
          onClose={() => setOpenFilterFields(false)}
          onFilter={handleApplyFilter}
          generateScheduleReport={{
            openModal: openScheduleReportModal,
            onCloseModal: () => setOpenScheduleReportModal(false)
          }}
        />
      </Container>
    </>
  );
};

export default CanHistory;
